import { AxiosError } from 'axios'

import { client } from './client'
import * as ENDPOINTS from './endpoints'
import { RecurrenceRule } from './connect'

export interface EventCreate extends JSON {
  connectionId: string
  title: string
  description: string
  eventDate: string
  startTime: string
  endTime: string
  location: string
  locationPoint?: {
    type: 'Point'
    coordinates: [number, number]
  }
  participantIds: string[]
}

export interface EventUpdate extends JSON {
  title?: string
  description?: string
  eventDate?: string
  startTime?: string
  endTime?: string
  location?: string
  locationPoint?: {
    type: 'Point'
    coordinates: [number, number]
  }
  timezone?: string
  recurrenceRule?: RecurrenceRule | null // null to remove recurrence
}

export interface InfoResponse {
  message?: string
  detail?: string
}

export interface Event {
  id: string
  connectionId: string
  title: string
  description: string
  eventDate: string
  startTime: string
  endTime: string
  location: string
  locationPoint?: {
    type: 'Point'
    coordinates: [number, number]
  }
  participant: EventParticipant[]
  // Recurring event fields
  isRecurring?: boolean
  recurrenceRule?: RecurrenceRule
  lastOccurrenceDate?: string | null
  occurrences?: {
    date: string
    startTime: string
    endTime: string
    status: string
    isException: boolean
  }[]
  // Additional fields for occurrence events
  isException?: boolean
  occurrenceStatus?: string
}

export interface EventParticipant {
  id: string
  user: string
  response: 'accepted' | 'pending' | 'rejected'
  responseAt: string
  userName: string
  joinedAt: string
  joinedAs: 'sender' | 'receiver'
  userAvatar: string
}

export interface ResponseEvent extends JSON {
  response: 'accepted' | 'rejected'
}

export interface EventTimeRequest extends JSON {
  eventDate?: string
  startDate?: string
  endDate?: string
}

export const createEvent = async (data: EventCreate): Promise<InfoResponse> => {
  try {
    const response = await client.post<InfoResponse, EventCreate>(ENDPOINTS.CREATE_EVENT, data)
    return { message: response.data.message }
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 400) {
        return { detail: 'Input info invalid' }
      }
    }
    return { detail: 'Fail to submit' }
  }
}

export const responseToEvent = async (
  data: ResponseEvent,
  eventId: string
): Promise<InfoResponse> => {
  try {
    const response = await client.post<InfoResponse, ResponseEvent>(
      ENDPOINTS.RESPONSE_EVENT + eventId + '/',
      data
    )
    return { message: response.data.message }
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 400) {
        return { message: 'Input info invalid' }
      }
    }
    return { message: 'Fail to submit' }
  }
}

export const getPendingEvents = async (): Promise<Event[]> => {
  try {
    const response = await client.get<Event[]>(ENDPOINTS.GET_PENDING_EVENTS)
    return response.data
  } catch {
    return []
  }
}

// TODO: getAcceptedEvents, getOneEvent
export const getAcceptedEvents = async (): Promise<Event[]> => {
  try {
    const response = await client.get<Event[]>(ENDPOINTS.GET_ACCEPTED_EVENTS)
    return response.data
  } catch {
    return []
  }
}

export const getOneEvent = async (eventId: string): Promise<Event> => {
  const response = await client.get<Event>(`${ENDPOINTS.GET_ONE_EVENT}${eventId}/`)
  return response.data
}

export const getEventsByTime = async (data: EventTimeRequest): Promise<Event[]> => {
  try {
    const response = await client.post<Event[], EventTimeRequest>(
      ENDPOINTS.GET_EVENTS_BY_TIME,
      data
    )
    return response.data
  } catch {
    return []
  }
}

export const getEventsByTimeRange = async (data: EventTimeRequest): Promise<Event[]> => {
  try {
    const response = await client.post<Event[], EventTimeRequest>(
      ENDPOINTS.GET_EVENTS_BY_TIME_RANGE,
      data
    )
    return response.data
  } catch {
    return []
  }
}

export const getEventsByUser = async (): Promise<Event[]> => {
  try {
    const response = await client.get<Event[]>(`${ENDPOINTS.GET_EVENTS_BY_USER}`)
    return response.data
  } catch {
    return []
  }
}

export const getPastEvents = async (): Promise<Event[]> => {
  try {
    const response = await client.get<Event[]>(`${ENDPOINTS.GET_PAST_EVENTS}`)
    return response.data
  } catch {
    return []
  }
}

export const updatePrivateEvent = async (
  eventId: string,
  data: EventUpdate
): Promise<InfoResponse> => {
  try {
    const url = ENDPOINTS.UPDATE_PRIVATE_EVENT.replace('{id}', eventId)

    const response = await client.patch<InfoResponse, EventUpdate>(url, data)
    return { message: response.data.message }
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 403) {
        return { detail: 'You do not have permission to edit this event' }
      }
      if (error.response.status === 404) {
        return { detail: 'Event not found' }
      }
      if (error.response.status === 400) {
        const data = error.response.data as { detail?: string }
        return { detail: data.detail ?? 'Invalid input' }
      }
    }
    return { detail: 'Failed to update event' }
  }
}

interface InviteParticipantsRequest extends JSON {
  invitedFriends: string[]
}

interface InviteParticipantsRequest extends JSON {
  invitedFriends: string[]
}

/**
 * Invite participants to a private event
 */
export const inviteParticipants = async (
  eventId: string,
  friendIds: string[]
): Promise<InfoResponse> => {
  try {
    const url = ENDPOINTS.INVITE_PARTICIPANTS.replace('{id}', eventId)
    const requestData = {
      invitedFriends: friendIds,
    } as InviteParticipantsRequest
    const response = await client.post<InfoResponse, InviteParticipantsRequest>(url, requestData)
    return { message: response.data.message }
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 403) {
        return { detail: 'You do not have permission to invite participants to this event' }
      }
      if (error.response.status === 404) {
        return { detail: 'Event not found' }
      }
      if (error.response.status === 400) {
        const data = error.response.data as { detail?: string }
        return { detail: data.detail ?? 'Invalid input' }
      }
    }
    return { detail: 'Failed to invite participants' }
  }
}

/**
 * Remove a participant from a private event
 * Uses URL parameter for userId instead of request body
 */
export const removeParticipant = async (eventId: string, userId: string): Promise<InfoResponse> => {
  try {
    const url = ENDPOINTS.REMOVE_PARTICIPANT.replace('{id}', eventId).replace('{userId}', userId)
    const response = await client.delete<InfoResponse>(url)
    return { message: response.data.message }
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 403) {
        return { detail: 'You do not have permission to remove participants from this event' }
      }
      if (error.response.status === 404) {
        return { detail: 'Participant not found' }
      }
      if (error.response.status === 400) {
        const data = error.response.data as { detail?: string }
        return { detail: data.detail ?? 'Invalid input' }
      }
    }
    return { detail: 'Failed to remove participant' }
  }
}

/**
 * Delete a private event
 * Only the event creator can delete the event
 */
export const deletePrivateEvent = async (eventId: string): Promise<InfoResponse> => {
  try {
    const url = ENDPOINTS.DELETE_PRIVATE_EVENT.replace('{id}', eventId)
    const response = await client.delete<InfoResponse>(url)
    return { message: response.data.message }
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 403) {
        return { detail: 'You do not have permission to delete this event' }
      }
      if (error.response.status === 404) {
        return { detail: 'Event not found' }
      }
      if (error.response.status === 400) {
        const data = error.response.data as { detail?: string }
        return { detail: data.detail ?? 'Invalid input' }
      }
    }
    return { detail: 'Failed to delete event' }
  }
}
